import type { AgentActionKey } from '@accio3d/shared/agent';
import type { AxiosInstance } from 'axios';

import type { OpenStream } from './client';

export interface AgentThread {
  id: string;
  title: string | null;
  status: 'active' | 'needs_input' | 'archived';
  screenPath: string | null;
  lastMessageAt: string | null;
  createdAt: string;
}

export interface AgentSuggestion {
  /**
   * Typed against the shared action registry, so an action the backend adds and
   * the UI cannot execute is a compile error here rather than a suggestion that
   * silently resolves to no link.
   */
  key: AgentActionKey;
  params: Record<string, unknown>;
  title: string;
  rationale: string;
  fingerprint?: string;
}

export type AgentSuggestionDisposition = 'dismissed' | 'accepted';

type AgentSuggestionState = { fingerprint: string; disposition: AgentSuggestionDisposition };

export type AgentPendingQuestion = { id: string; question: string; choices?: string[] };

type AgentMessage = {
  id: string;
  role: 'human' | 'ai' | 'system';
  content: string;
  parts: { suggestions?: AgentSuggestion[]; pendingQuestion?: AgentPendingQuestion } | null;
  seq: number;
  createdAt: string;
};

interface AgentMemory {
  id: string;
  scope: 'user' | 'organization';
  fact: string;
  confirmedAt: string | null;
  createdAt: string;
}

export interface ScreenHintPayload {
  path: string;
  params: Record<string, string>;
  hints?: Record<string, string>;
}

export function createAgentService(client: AxiosInstance, openStream: OpenStream) {
  return {
    openStream,

    async listThreads(): Promise<AgentThread[]> {
      const { data } = await client.get<{ data: AgentThread[] }>('/ai/assistant/threads');
      return data.data;
    },

    async createThread(screen?: ScreenHintPayload): Promise<AgentThread> {
      const { data } = await client.post<AgentThread>('/ai/assistant/threads', {
        screen: screen ? { path: screen.path, params: screen.params } : undefined,
      });
      return data;
    },

    async getThread(
      threadId: string,
      signal?: AbortSignal,
    ): Promise<{
      thread: AgentThread;
      messages: AgentMessage[];
      suggestionStates: AgentSuggestionState[];
      pendingQuestion: AgentPendingQuestion | null;
    }> {
      const { data } = await client.get<{
        thread: AgentThread;
        messages: AgentMessage[];
        suggestionStates?: AgentSuggestionState[];
        pendingQuestion?: AgentPendingQuestion | null;
      }>(`/ai/assistant/threads/${threadId}`, { signal });
      return {
        ...data,
        suggestionStates: data.suggestionStates ?? [],
        pendingQuestion: data.pendingQuestion ?? null,
      };
    },

    async setSuggestionState(
      threadId: string,
      fingerprint: string,
      disposition: AgentSuggestionDisposition,
    ): Promise<void> {
      await client.put(`/ai/assistant/threads/${threadId}/suggestion-states`, {
        fingerprint,
        disposition,
      });
    },

    /* Stops the turn currently running on a conversation */
    async cancelRun(threadId: string): Promise<void> {
      await client.delete(`/ai/assistant/threads/${threadId}/run`);
    },

    async deleteThread(threadId: string): Promise<void> {
      await client.delete(`/ai/assistant/threads/${threadId}`);
    },

    async listMemories(): Promise<AgentMemory[]> {
      const { data } = await client.get<{ data: AgentMemory[] }>('/ai/assistant/memories');
      return data.data;
    },

    async forgetMemory(memoryId: string): Promise<void> {
      await client.delete(`/ai/assistant/memories/${memoryId}`);
    },

    async confirmMemory(memoryId: string): Promise<void> {
      await client.post(`/ai/assistant/memories/${memoryId}/confirm`);
    },

    async suggestedPrompts(path: string): Promise<string[]> {
      const { data } = await client.get<{ prompts: string[] }>('/ai/assistant/prompts', {
        params: { path },
      });
      return data.prompts;
    },
  };
}

export type AgentService = ReturnType<typeof createAgentService>;
